import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import {
  artBoardActions,
  selectEditingArrowLabel,
} from '@store/reducers/artboard'
import type React from 'react'
import { useEffect, useLayoutEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { useArtboardMode } from '@/hooks'
import { useArtBoardDisabled } from '@/hooks/selectors/useArtBoardDisabled'
import { useScaleZoom } from '@/hooks/useScaleZoom'
import type { AppState } from '@/types'
import { useGetArrows } from '../../hooks/useGetArrows'
import { ArrowLabelInput } from './components/ArrowLabelInput'
import { ArrowLabelText } from './components/ArrowLabelText'
import css from './styles.module.css'
import { placeLabels } from './utils'

/**
 * Labels are one layer over the page cards: an arrow is painted under them, and
 * `Arrows` remounts every arrow on each render, which would take the focus away
 * from a field living inside one. The layer only reads where the lines are, so
 * it follows them through a MutationObserver, whatever made them move.
 */
export const ArrowLabels: React.FC = () => {
  const dispatch = useDispatch()
  const layerRef = useRef<HTMLDivElement>(null)

  const arrows = useGetArrows()
  const editingId = useSelector(selectEditingArrowLabel)
  const disabled = useArtBoardDisabled()
  const { scale } = useScaleZoom()
  const { simpleView } = useArtboardMode()

  const arrowsHide = useSelector<AppState, boolean>(
    ({ canvas }) => canvas.arrow.hideArrows
  )
  const readOnly = useSelector(
    migrationProjectSelector.getProjectLegacyAccessTypeIsReadOnly
  )

  const hidden = simpleView || arrowsHide
  const canEdit = !readOnly && !disabled

  const editingLost =
    editingId !== null &&
    (hidden || !canEdit || !arrows.some(({ id }) => id === editingId))

  // A field that went away by itself must not come back without a click.
  useEffect(() => {
    if (editingLost) {
      dispatch(artBoardActions.setEditingArrowLabel(null))
    }
  }, [dispatch, editingLost])

  useLayoutEffect(() => {
    if (layerRef.current) {
      placeLabels(layerRef.current)
    }
  })

  useEffect(() => {
    const lines = document.querySelector('.Tree-arrows')
    const layer = layerRef.current

    if (hidden || !lines || !layer) {
      return
    }

    const observer = new MutationObserver(() => placeLabels(layer))

    observer.observe(lines, {
      attributeFilter: ['d', 'style'],
      attributes: true,
      childList: true,
      subtree: true,
    })

    return () => observer.disconnect()
  }, [hidden])

  if (hidden) {
    return null
  }

  return (
    <div
      ref={layerRef}
      className={css.layer}
      style={{ '--arrow-label-scale': scale } as React.CSSProperties}
    >
      {arrows.map((arrow) => {
        if (canEdit && arrow.id === editingId) {
          return <ArrowLabelInput key={arrow.id} arrow={arrow} />
        }

        return arrow.label ? (
          <ArrowLabelText key={arrow.id} arrow={arrow} canEdit={canEdit} />
        ) : null
      })}
    </div>
  )
}
